import React, { useCallback, useEffect, useRef, useState } from "react";
import { nearestCorner } from "./CropAspectPicker";

// Four free corners over an image; the API straightens the quad they enclose
// (POST /admin/cover-curation/:id/faces/:face/crop). Points are [x, y] in 0..1 of the
// image, clockwise from top-left. Drag a corner, or focus it and nudge with the arrow
// keys (Shift for bigger steps). onPointsChange and onImageLoad (natural size) let the
// caller follow the crop; children render above the actions.
//
// Optional fifth point (onEdgePointChange): the far end of a box edge leaving one of the
// corners into the neighbouring face (the side's top edge, say). It isn't cropped; it
// tells the proportion estimate how far the face is turned (CropAspectPicker
// edgeAspect). The edge is drawn from the nearest corner.
const CORNER_LABELS = ["top-left", "top-right", "bottom-right", "bottom-left"];
const DEFAULT_POINTS = [[0.05, 0.05], [0.95, 0.05], [0.95, 0.95], [0.05, 0.95]];
const NUDGE = 0.002;
const NUDGE_LARGE = 0.01;
const EDGE = 4; // handle index of the fifth point

const clamp = (value) => Math.min(1, Math.max(0, value));

// Starts halfway between the quad's top-left corner and the image's left edge (where
// the side shows on most box shots), or to the right when there's no room on the left.
function defaultEdgePoint(points) {
  const [x, y] = points[0];
  return x > 0.08 ? [x / 2, clamp(y + 0.03)] : [clamp((points[1][0] + 1) / 2), clamp(points[1][1] + 0.03)];
}

function PerspectiveCropEditor({
  src, initialPoints, busy, onApply, onCancel, onPointsChange, onImageLoad, onEdgePointChange, children,
}) {
  const frameRef = useRef(null);
  const [points, setPoints] = useState(initialPoints || DEFAULT_POINTS);
  const [edgePoint, setEdgePoint] = useState(null);
  const [dragging, setDragging] = useState(null);

  useEffect(() => {
    onPointsChange?.(points);
  }, [points, onPointsChange]);

  useEffect(() => {
    onEdgePointChange?.(edgePoint);
  }, [edgePoint, onEdgePointChange]);

  const update = useCallback((index, move) => {
    if (index === EDGE) setEdgePoint((prev) => move(prev));
    else setPoints((prev) => prev.map((point, i) => (i === index ? move(point) : point)));
  }, []);

  const moveTo = useCallback((index, clientX, clientY) => {
    const rect = frameRef.current.getBoundingClientRect();
    const next = [clamp((clientX - rect.left) / rect.width), clamp((clientY - rect.top) / rect.height)];
    update(index, () => next);
  }, [update]);

  function handleKeyDown(index, e) {
    const step = e.shiftKey ? NUDGE_LARGE : NUDGE;
    const delta = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
    if (!delta) return;
    e.preventDefault();
    update(index, (point) => [clamp(point[0] + delta[0]), clamp(point[1] + delta[1])]);
  }

  function handle(index, [x, y], label, extraClass = "") {
    return (
      <button
        type="button"
        key={label}
        className={`admin-crop-handle ${extraClass} ${dragging === index ? "admin-crop-handle--active" : ""}`}
        style={{ left: `${x * 100}%`, top: `${y * 100}%` }}
        aria-label={`Move ${label}`}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          setDragging(index);
        }}
        onPointerMove={(e) => {
          if (dragging === index) moveTo(index, e.clientX, e.clientY);
        }}
        onPointerUp={() => setDragging(null)}
        onPointerCancel={() => setDragging(null)}
        onKeyDown={(e) => handleKeyDown(index, e)}
        disabled={busy}
      />
    );
  }

  const quad = points.map(([x, y]) => `${x} ${y}`).join(" L ");
  const edgeFrom = edgePoint ? points[nearestCorner(points, edgePoint)] : null;

  return (
    <div className="admin-crop">
      <div className="admin-crop-stage">
        <div className="admin-crop-frame" ref={frameRef}>
          <img
            className="admin-crop-image"
            src={src}
            alt="Crop source"
            draggable={false}
            onLoad={(e) => onImageLoad?.({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
          />
          <svg className="admin-crop-overlay" viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
            <path className="admin-crop-shade" d={`M0 0 H1 V1 H0 Z M ${quad} Z`} fillRule="evenodd" />
            <path className="admin-crop-outline" d={`M ${quad} Z`} vectorEffect="non-scaling-stroke" />
            {edgePoint && (
              <path
                className="admin-crop-edge"
                d={`M ${edgeFrom[0]} ${edgeFrom[1]} L ${edgePoint[0]} ${edgePoint[1]}`}
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>
          {points.map((point, index) => handle(index, point, `${CORNER_LABELS[index]} corner`))}
          {edgePoint && handle(EDGE, edgePoint, "side edge end", "admin-crop-handle--edge")}
        </div>
      </div>

      {children}

      <div className="admin-row-actions admin-row-actions--gap admin-crop-actions">
        <button
          type="button"
          className="admin-signout-btn admin-crop-edge-toggle"
          onClick={() => setEdgePoint((prev) => (prev ? null : defaultEdgePoint(points)))}
          disabled={busy}
          title="Drag it to the far end of an edge going from a corner into the next face (e.g. the side's top edge) to estimate how much the face is turned"
        >
          {edgePoint ? "Remove side edge" : "Mark side edge"}
        </button>
        <button type="button" className="admin-signout-btn" onClick={() => setPoints(DEFAULT_POINTS)} disabled={busy}>
          Reset corners
        </button>
        <button type="button" className="admin-signout-btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="admin-signout-btn admin-crop-apply" onClick={() => onApply(points)} disabled={busy}>
          {busy ? "Cropping…" : "Apply crop"}
        </button>
      </div>
    </div>
  );
}

export default PerspectiveCropEditor;
